/**
 * The team's validation datasets a GRPO run can score, as ticks: the launch
 * dialog (TRA-1377) and the scheduled run's form (TRA-1475) both offer
 * them. Kept apart from both so neither has to load the other.
 *
 * A ticked dataset that has left the registry is listed too, marked, so it
 * can be cleared. Hidden, it would stay in every save and fail each one.
 */

import type { TrainingGrpoValidationOption } from '@/api/generated/model'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

import { grpoValidationSummary, missingValidationIds, validationSelection } from './launch'

/** Each dataset on its own line; the ticked ones go with the launch or the schedule. */
export function ValidationDatasetsField({
  options,
  selected,
  disabled = false,
  onChange,
}: {
  options: TrainingGrpoValidationOption[]
  selected: string[]
  disabled?: boolean
  onChange: (ids: string[]) => void
}): React.JSX.Element {
  const missing = missingValidationIds(options, selected)
  const rows = [
    ...options.map((option) => ({ id: option.registry_id, note: grpoValidationSummary(option) })),
    ...missing.map((id) => ({ id, note: 'Not in the registry' })),
  ]
  const listed = rows.map((row) => row.id)
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">Validation datasets</legend>
      {rows.map((row) => {
        const id = `launch-grpo-validation-${row.id}`
        return (
          <div key={row.id} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={selected.includes(row.id)}
              disabled={disabled}
              onCheckedChange={(checked) => {
                onChange(validationSelection(listed, selected, row.id, checked === true))
              }}
            />
            <Label htmlFor={id} className="flex items-baseline gap-2 font-normal">
              <span className="font-mono text-xs">{row.id}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {row.note}
              </span>
            </Label>
          </div>
        )
      })}
      <p className="text-xs text-muted-foreground">
        The run scores each ticked dataset on its own. With none ticked, it validates on the
        dataset&apos;s own validation tasks.
      </p>
    </fieldset>
  )
}
